/* eslint-disable camelcase */
import { useContext, useState } from 'react'
import * as Notifications from 'expo-notifications'
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator'
import moment from 'moment'
import { FuelContext } from '../../../../context/Logistics/fuel/FuelProvider'
import { AuthContext } from '../../../../context/Auth/AuthContext'
import { Order } from '../../../../context/Logistics/fuel/types'
import { useBlobAzure } from '../../../../hooks/useAzureSaveImage'
import { aquaApi } from '../../../../api/aquaApi'
import { paths } from '../../../../api/pathsApi'
import { OrderBody, OrderResponse, ImagesBody } from '../pages/Summary/types'

const compressImage = async (uri: string): Promise<string> => {
  const result = await manipulateAsync(uri, [], { compress: 0.7, format: SaveFormat.JPEG })
  return result.uri
}

interface Args {
  onSuccess?: (ids: string[]) => void
  onError?: (ids: string[]) => void
}

const buildStatusDetail = (
  record: Order,
  orderStatus: { id: number; name: string }[],
): { id_status: number; timestamp: string }[] =>
  Object.values(record.statusDetail ?? {})
    .map(({ status, time }) => {
      const match = orderStatus.find((s) => s.name === status)
      return match ? { id_status: match.id, timestamp: time } : null
    })
    .filter((item): item is { id_status: number; timestamp: string } => item !== null)
    .sort((a, b) => a.id_status - b.id_status)

const notify = async (title: string, body: string) => {
  try {
    await Notifications.scheduleNotificationAsync({
      content: { title, body },
      trigger: null,
    })
  } catch {
    // notificaciones opcionales, no interrumpir el flujo
  }
}

export const useSendFuelRecord = ({ onSuccess, onError }: Args) => {
  const {
    user: { userId },
  } = useContext(AuthContext)
  const { changeStateForm, params } = useContext(FuelContext)
  const { uploadFiles } = useBlobAzure({ containerName: 'logistic' })
  const [isLoading, setIsLoading] = useState(false)

  const handleSend = async (records: Order[]) => {
    if (records.length === 0) return
    setIsLoading(true)

    const successIds: string[] = []
    const errorIds: string[] = []

    for (const record of records) {
      const id = record.orderNumber!
      changeStateForm([{ id, stateForm: 'sending' }])

      try {
        // 1. Comprimir y subir fotos a Azure
        const photos = Object.values(record.photos ?? {})
        const compressedUris = await Promise.all(photos.map((p) => compressImage(p.uri)))
        const azureUrls = await uploadFiles(
          compressedUris.map((uri, index) => ({
            imageName: `${record.orderNumber}-${index + 1}`,
            imageUri: uri,
          })),
        )

        if (azureUrls.length === 0) throw new Error('Azure upload failed')

        // 2. Crear orden en el servidor
        const statusDetail = buildStatusDetail(record, params.orderStatus)
        const orderBody: OrderBody = {
          id_user: userId!,
          order: {
            number: record.orderNumber!,
            order_date: moment(record.deliveryDate, ['DD/MM/YYYY', 'DD-MM-YYYY']).format(
              'YYYY-MM-DD',
            ),
            id_naval_artifact: record.pontoonId!,
            id_supplier: record.supplierId!,
            id_ship: record.shipId!,
            id_status: statusDetail[statusDetail.length - 1]?.id_status ?? 0,
            voucher_number: record.voucherNumber,
            meter_value: record.meterNumber,
            water_test: false,
            status_detail: statusDetail,
          },
        }

        const { data: orderData } = await aquaApi.post<OrderResponse>(
          paths().LOGISTIC.CREATE_ORDER,
          orderBody,
        )

        // 3. Adjuntar recursos (fotos) a la orden
        const resourceBody: ImagesBody = {
          id_user: userId!,
          files: azureUrls.map((url, index) => {
            const photo = photos[index]!
            const date = new Date(+photo.dateTime)
            return {
              url,
              id_resource_type:
                params.resourseTypes.find((r) => r.name.includes(photo.resourceType))?.id ?? 0,
              timestamp: date.toISOString(),
            }
          }),
        }

        await aquaApi.post(paths().LOGISTIC.CREATE_RECORD(orderData.id_order), resourceBody)

        changeStateForm([{ id, stateForm: 'sent' }])
        successIds.push(id)
      } catch {
        changeStateForm([{ id, stateForm: 'pending' }])
        errorIds.push(id)
      }
    }

    setIsLoading(false)

    if (successIds.length > 0) {
      onSuccess?.(successIds)
      await notify(
        'Recepción combustible',
        `${successIds.length} registro${successIds.length > 1 ? 's' : ''} enviado${
          successIds.length > 1 ? 's' : ''
        } con éxito`,
      )
    }

    if (errorIds.length > 0) {
      onError?.(errorIds)
      await notify(
        'Error al enviar',
        `${errorIds.length} registro${errorIds.length > 1 ? 's' : ''} no pudo${
          errorIds.length > 1 ? 'ron' : ''
        } enviarse. Revisa Registros Combustible.`,
      )
    }
  }

  return { handleSend, isLoading }
}
